import { useCallback, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import { Checkbox } from '@/shared/ui/checkbox';
import { Table } from '@/shared/ui/table/Table';
import type { ColumnDef, SortState } from '../types';

export interface SelectorDataTableProps<T> {
  items: readonly T[];
  columns: readonly ColumnDef<T>[];
  mode: 'single' | 'multi';
  selected: readonly T[];
  onSelect: (item: T) => void;
  onToggleSelection?: (item: T) => void;
  sort?: SortState;
  onSortChange?: (sort: SortState | undefined) => void;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error;
  emptyMessage?: string;
  loadingMessage?: string;
  errorMessage?: string;
  className?: string;
  ariaLabel?: string;
  getKey?: (item: T) => string | number;
  activeRowIndex?: number;
}

export function SelectorDataTable<T>({
  items,
  columns,
  mode,
  selected,
  onSelect,
  onToggleSelection,
  sort,
  onSortChange,
  isLoading,
  isError,
  error: _error,
  emptyMessage,
  loadingMessage,
  errorMessage,
  className,
  ariaLabel,
  getKey,
  activeRowIndex,
}: SelectorDataTableProps<T>) {
  const { t } = useTranslation('asyncSelector');

  const resolvedLoadingMsg = loadingMessage ?? t('table.loading');
  const resolvedErrorMsg = errorMessage ?? t('table.error');
  const resolvedEmptyMsg = emptyMessage ?? t('table.empty');
  const selectItemLabel = t('table.selectItem');

  const isSelected = useCallback(
    (item: T) => {
      if (getKey) {
        const key = getKey(item);
        return selected.some((existing) => getKey(existing) === key);
      }
      return selected.includes(item);
    },
    [getKey, selected]
  );

  const handleRowKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTableRowElement>, item: T) => {
      if (mode === 'single' && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onSelect(item);
      }
    },
    [mode, onSelect]
  );

  const resolveRowKey = (item: T, index: number) => {
    if (getKey) return String(getKey(item));
    return String(index);
  };

  const stateMessageClass = 'text-phx-sm flex items-center justify-center gap-phx-2 py-phx-8';

  if (isLoading) {
    return (
      <div
        data-testid="selector-data-table-loading"
        className={cn(stateMessageClass, 'text-phx-text-muted', className)}
      >
        <span className="border-phx-border-3 border-t-phx-primary inline-block h-4 w-4 animate-spin rounded-full border-2" />
        {resolvedLoadingMsg}
      </div>
    );
  }

  if (isError) {
    return (
      <div
        data-testid="selector-data-table-error"
        className={cn(stateMessageClass, 'text-phx-danger', className)}
      >
        {resolvedErrorMsg}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        data-testid="selector-data-table-empty"
        className={cn('text-phx-sm text-phx-text-muted py-phx-8 text-center', className)}
      >
        {resolvedEmptyMsg}
      </div>
    );
  }

  return (
    <div className={cn('overflow-x-auto', className)} data-testid="selector-data-table">
      <Table className="border-phx-border border" role="table" aria-label={ariaLabel}>
        <Table.Head>
          <Table.Row>
            {mode === 'multi' ? <Table.HCell className="w-10" aria-hidden /> : null}
            {columns.map((col) => {
              const isSorted = sort?.columnId === col.id;
              const sortDirection = isSorted ? sort?.direction : null;
              return (
                <Table.HCell
                  key={col.id}
                  sortable={col.sortable && !!onSortChange}
                  sortDirection={sortDirection}
                  data-testid={`selector-table-header-${col.id}`}
                  onClick={
                    col.sortable && onSortChange
                      ? () => {
                          if (sort?.columnId === col.id) {
                            if (sort.direction === 'asc') {
                              onSortChange({ columnId: col.id, direction: 'desc' });
                            } else {
                              onSortChange(undefined);
                            }
                          } else {
                            onSortChange({ columnId: col.id, direction: 'asc' });
                          }
                        }
                      : undefined
                  }
                  aria-sort={
                    isSorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'
                  }
                >
                  {col.header}
                </Table.HCell>
              );
            })}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {items.map((item, rowIndex) => {
            const itemSelected = isSelected(item);
            const keyboardActive = activeRowIndex === rowIndex;
            return (
              <Table.Row
                key={resolveRowKey(item, rowIndex)}
                selected={itemSelected || keyboardActive}
                data-testid="selector-table-row"
                tabIndex={mode === 'single' ? 0 : undefined}
                className={cn(mode === 'single' && 'cursor-pointer')}
                onClick={() => onSelect(item)}
                onKeyDown={(e) => handleRowKeyDown(e, item)}
              >
                {mode === 'multi' ? (
                  <Table.Cell
                    className="w-10 align-middle"
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <div
                      className="flex items-center justify-center [&_.phx-check]:shrink-0 [&_.phx-check-label]:items-center [&_.phx-check-wrap]:block [&_.phx-check-wrap]:gap-0"
                      data-testid="selector-table-checkbox"
                    >
                      <Checkbox
                        checked={itemSelected}
                        onCheckedChange={() => (onToggleSelection ?? onSelect)(item)}
                        label={<span className="sr-only">{selectItemLabel}</span>}
                        className="leading-none [&_.phx-check-box]:m-0"
                      />
                    </div>
                  </Table.Cell>
                ) : null}
                {columns.map((col) => (
                  <Table.Cell key={col.id} data-testid={`selector-table-cell-${col.id}`}>
                    {col.cell(item)}
                  </Table.Cell>
                ))}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
}
